import { useState, useContext } from "react";
import PropTypes from "prop-types";
import { Modal } from "react-bootstrap";
import API, { API_URL } from "../services/api";
import Confirmation from "./Confirmation";
import classes from "./FileItem.module.css";
import CustomButton from "./common/CustomButton";
import pdfIcon from "../imgs/attachment-icons/pdf_icon.png";
import ModalProvider from "./common/ModalProvider";
import { contactSupportText } from "./common/utils/constants";
import toast from "react-hot-toast";
import { truncateWithTooltip } from "../helpers/common";
import MultiSelect from "../components/common/MultiSelect";
import { GroupContext } from "../services/contexts/GroupContext";

function FileItem(props) {
  const { file, getFiles, role, isChecked, onCheckChange } = props;
  const url = `${API_URL}/getFile?file_name=${file.file_name}&file_id=${file.id}`;
  const [showEditFileModal, setShowEditFileModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState({
    ...file,
    newDocumentTitle: file.document_title,
    selectedGroups: file.groups || [],
  });  
  const { globalData } = useContext(GroupContext);

  const [isOpenViewer, setIsOpenViewer] = useState(false);

  const handleEditFileShow = () => setShowEditFileModal(true);
  const handleEditFileClose = () => {
    setShowEditFileModal(false);
    setSelectedFile(prev=> ({...prev, newDocumentTitle: file.document_title, selectedGroups: file.groups || []}));
  };
  function handleOpenFile() {
    setIsOpenViewer(true);
  }
  function handleCloseFile() {
    setIsOpenViewer(false);
  }

  const updateFile = () => {
    API.put(`/admin/updateFile`, {
      id: selectedFile.id,
      documentTitle: selectedFile.newDocumentTitle,
      group_ids: selectedFile.selectedGroups.map((group) => group.group_id)
    })
      .then((response) => {
        alert(response.data.msg);
        getFiles();
      })
      .catch((err) => {
        alert(err.response.data.msg);
      });
  };

  const saveChanges = () => {
    handleEditFileClose();
    updateFile();
  };

  return (
    <>
      {role === 1 && (
        <td>
          <div className={classes.checkbox_wrapper}>
            <input
              checked={isChecked}
              type="checkbox"
              onChange={onCheckChange}
            />
          </div>
        </td>
      )}
      <td
        className={classes.file_td_cell}
        onClick={() => handleOpenFile(url, file.document_title)}
      >
        <div className={classes.file_box}>
          <img src={pdfIcon} alt="file" className={classes.file_icon} />
          <div className={classes.file_link}>
            {truncateWithTooltip(file.document_title, 55)}
          </div>
        </div>
      </td>

      <td>{file.uploaded_date ? new Date(file.uploaded_date).toLocaleDateString() : ""}</td>
      <td>{file.created_date ? new Date(file.created_date).toLocaleDateString() : ""}</td>
      <td>
        {file.last_modify_date ? new Date(file.last_modify_date).toLocaleDateString() : ""}
      </td>

      {role === 1 && (
        <td>
          <div className={classes.action_button_wrapper}>
            <CustomButton
              variant="outline-primary"
              onClick={handleEditFileShow}
              className={classes.action_button}
            >
              Edit
            </CustomButton>

            <Confirmation
              content={`Please confirm ${
                file.inactive === 1 ? "activate" : "deactivate"
              } this file ?`}
              Children={
                <CustomButton
                  className={classes.action_button}
                  variant={
                    file.inactive === 1 ? "outline-success" : "outline-danger"
                  }
                >
                  {file.inactive === 0 ? "Deactivate" : "Activate"}
                </CustomButton>
              }
              onConfirm={() => {
                API.put("/admin/inactiveFile", {
                  file_id: file.id,
                  inactive: file.inactive === 0 ? 1 : 0,
                })
                  .then(() => {
                    getFiles();
                  })
                  .catch(() => {
                    alert("Something goes wrong! Please try again.");
                  });
              }}
            />
          </div>
        </td>
      )}
      <Modal
        show={showEditFileModal}
        onHide={handleEditFileClose}
        backdrop="static"
      >
        <Modal.Header className="_g-modal-header_">
          <div className="_g-close-button-anchor_">
            <div className="_g-close-button_" onClick={handleEditFileClose}>
              &#x2715;
            </div>
          </div>
          Edit File
        </Modal.Header>
        <Modal.Body className="_g-modal-body_">
          <input
            type="text"
            className="form-control"
            placeholder="New File Name"
            value={selectedFile.newDocumentTitle}
            onChange={(e) => {
              setSelectedFile((prev)=> ({...prev, newDocumentTitle : e.target.value}));
              if (e.target.value.length === 128) {
                toast.error(
                  `File document title can not be longer than 128 characters.`
                );
              }
            }}
            maxLength={128}
          />

          <div className={classes.group_select}>
            <MultiSelect
              label="Assign group"
              options={globalData.allGroups}
              selected={selectedFile.selectedGroups}
              onChange={(newGroups) => {
                setSelectedFile((prev) => ({
                  ...prev,
                  selectedGroups: newGroups,
                }));
              }}
              placeholder="Please Select"
            />
          </div>
          <div className="_g-modal-button-wrapper_">
            <CustomButton
              variant="outline-secondary"
              onClick={handleEditFileClose}
            >
              Close
            </CustomButton>
            <CustomButton
              variant="primary"
              onClick={saveChanges}
              disabled={!dataIsDirty(file, selectedFile)}
            >
              Save
            </CustomButton>
          </div>
        </Modal.Body>
      </Modal>

      <ModalProvider
        size="lg"
        show={isOpenViewer}
        handleClose={handleCloseFile}
        modalProps={{ onHide: handleCloseFile, backdrop: true }}
      >
        <div className="_g-pdf-view-wrapper_">
          {file && (
            <>
              <div
                className="_g-poll-modal-title_"
                style={{ textAlign: "center" }}
              >
                {truncateWithTooltip(file.document_title)}
              </div>
              <object
                type="application/pdf"
                data={url}
                className="_g-pdf-view_"
              >
                <div className="_g-pdf-view-failed_">
                  Failed to load the file, {contactSupportText}
                </div>
              </object>
            </>
          )}
        </div>
      </ModalProvider>
    </>
  );
}

const dataIsDirty = (originalFile, newFile) => {
  const isDirty = originalFile.document_title !== newFile.newDocumentTitle ||
  JSON.stringify(originalFile.groups) !== JSON.stringify(newFile.selectedGroups); //Check groups

  return isDirty;
};

FileItem.propTypes = {
  file: PropTypes.object.isRequired,
  getFiles: PropTypes.func.isRequired,
  role: PropTypes.number.isRequired,
  isChecked: PropTypes.bool,
  onCheckChange: PropTypes.func,
};

export default FileItem;
